import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { PageDataProvider, usePageData } from '@/app/page-data';
import { useHead } from '@/app/head';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { HomePage } from '@/pages/HomePage';
import { ServiceMenuPage } from '@/pages/ServiceMenuPage';
import { GalleryPage } from '@/pages/GalleryPage';
import { ContactPage } from '@/pages/ContactPage';
import { GenericPage } from '@/pages/GenericPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PageSkeleton } from '@/components/common/PageSkeleton';
import type { PageData } from '@cng/shared';

// The admin CMS is a separate concern and a large bundle; it loads on demand.
const AdminApp = lazy(() => import('@/admin/AdminApp'));

/**
 * The public site has no per-route component mapping in the router: the server
 * decides what a path is (a Page document's `pageType`) and this switch renders
 * it. That is what lets the owner add a landing page from the admin without a
 * code change.
 */
function ResolvedRoute() {
  const { data, loading, error } = usePageData();
  const site = data?.site;

  useHead(data?.seo, {
    siteName: site?.settings.businessName,
    googleAnalyticsId: site?.settings.googleAnalyticsId,
    googleTagManagerId: site?.settings.googleTagManagerId,
    faviconUrl: site?.settings.favicon?.url ?? undefined,
  });

  if (loading && !data) return <PageSkeleton />;

  if (error && !data) {
    return (
      <div className="cng-error-state" role="alert">
        <h1>We couldn&rsquo;t load this page</h1>
        <p>{error}</p>
        <button type="button" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  if (!data) return <PageSkeleton />;

  switch (data.route.kind) {
    case 'home':
      return <HomePage data={data.route} />;
    case 'service-menu':
      return <ServiceMenuPage data={data.route} />;
    case 'gallery':
      return <GalleryPage data={data.route} />;
    case 'contact':
      return <ContactPage data={data.route} />;
    case 'page':
      return <GenericPage data={data.route} />;
    default:
      return <NotFoundPage />;
  }
}

function PublicSite() {
  return (
    <PublicLayout>
      <ResolvedRoute />
    </PublicLayout>
  );
}

export function App({ initialData }: { initialData: PageData | null }) {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  if (isAdmin) {
    return (
      <Suspense fallback={<div className="admin-boot">Loading admin…</div>}>
        <AdminApp />
      </Suspense>
    );
  }

  return (
    <PageDataProvider initialData={initialData}>
      <Routes>
        <Route path="*" element={<PublicSite />} />
      </Routes>
    </PageDataProvider>
  );
}
